import { createFileRoute } from "@tanstack/react-router";
import { format, isToday, isTomorrow, parseISO } from "date-fns";
import {
  ChevronDown,
  ExternalLink,
  LineChart as LineChartIcon,
  MessageCircle,
  Repeat2,
  ThumbsUp,
} from "lucide-react";
import { useState } from "react";
import { EmptyState } from "@/components/brand/empty-state";
import { ErrorState } from "@/components/brand/error-state";
import { PageHeader } from "@/components/layout/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PostHistoryChart } from "@/features/analytics";
import { useDraftHistory, useMyDrafts, useMyGrowth } from "@/features/drafts";
import type { DraftPublic } from "@/lib/api";

/**
 * The growth journal: the posts this person put out, and how they are doing.
 *
 * Numbers only go up here because they are read off the live post: reactions
 * and comments refreshed daily for the first week, then weekly for life. This
 * is the payoff for pasting the link, and the whole reason the loop is worth
 * finishing.
 */
export const Route = createFileRoute("/_authenticated/me/growth")({
  component: GrowthPage,
});

function GrowthPage() {
  const growth = useMyGrowth();
  const drafts = useMyDrafts();

  if (growth.isPending || drafts.isPending) return <Skeleton className="h-96 rounded-lg" />;
  if (growth.isError) return <ErrorState onRetry={() => void growth.refetch()} />;
  if (drafts.isError) return <ErrorState onRetry={() => void drafts.refetch()} />;

  const posted = drafts.data
    .filter((d) => d.status === "posted")
    .sort((a, b) => (b.postedAt ?? "").localeCompare(a.postedAt ?? ""));
  const g = growth.data;

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        eyebrow="My space"
        title="Growth journal"
        description="What you have posted, and how it is landing. Updated on its own."
      />

      {posted.length === 0 ? (
        <EmptyState
          title="No posts yet"
          description="Write a draft, post it on LinkedIn, then mark it posted with the link. It will appear here and start tracking."
        />
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Posts" value={g.posted} />
            <Stat label="This month" value={g.thisMonth} />
            <Stat label="Reactions" value={g.reactions} icon={<ThumbsUp className="size-4" />} />
            <Stat label="Comments" value={g.comments} icon={<MessageCircle className="size-4" />} />
          </div>

          <ul className="space-y-2">
            {posted.map((d) => (
              <li key={d.id}>
                <PostedRow draft={d} />
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, icon }: { label: string; value: number; icon?: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="py-4 text-center">
        <p className="flex items-center justify-center gap-1.5 font-semibold text-2xl text-foreground">
          {icon} {value}
        </p>
        <p className="mt-0.5 text-muted-foreground text-xs uppercase tracking-[0.08em]">{label}</p>
      </CardContent>
    </Card>
  );
}

function PostedRow({ draft }: { draft: DraftPublic }) {
  const readings = draft.readings ?? 0;
  // A curve exists once there are two readings, and then it is the point of
  // this page, so it is open. One reading is a number, not a curve, and is
  // said in words below instead of hidden behind a toggle.
  const [open, setOpen] = useState(readings >= 2);
  const history = useDraftHistory(draft.id, open);
  const nextRead = draft.nextMetricsAt ? parseISO(draft.nextMetricsAt) : null;
  const nextReadText = nextRead
    ? isToday(nextRead) || nextRead < new Date()
      ? "tonight"
      : isTomorrow(nextRead)
        ? "tomorrow night"
        : `on ${format(nextRead, "d MMM")}`
    : null;

  return (
    <Card>
      <CardContent className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="line-clamp-2 text-foreground text-sm">
              {draft.title ?? (draft.body.trim() || "Your post")}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-muted-foreground text-xs">
              {draft.postedAt ? (
                <span>Posted {format(parseISO(draft.postedAt), "d MMM")}</span>
              ) : null}
              <span className="flex items-center gap-1">
                <ThumbsUp className="size-3.5" /> {draft.reactions ?? "?"}
              </span>
              <span className="flex items-center gap-1">
                <MessageCircle className="size-3.5" /> {draft.comments ?? "?"}
              </span>
              {draft.shares != null ? (
                <span className="flex items-center gap-1">
                  <Repeat2 className="size-3.5" /> {draft.shares}
                </span>
              ) : null}
            </div>
          </div>
          {draft.postUrl ? (
            <a
              href={draft.postUrl}
              target="_blank"
              rel="noreferrer"
              aria-label="Open the post on LinkedIn"
              className="shrink-0 text-muted-foreground hover:text-primary"
            >
              <ExternalLink className="size-4" />
            </a>
          ) : null}
        </div>

        <div className="border-border border-t pt-2">
          {readings >= 2 ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-muted-foreground"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
              >
                <LineChartIcon className="size-3.5" />
                {open ? "Hide" : "View"} engagement over time
                <ChevronDown
                  className={`size-3.5 transition-transform ${open ? "rotate-180" : ""}`}
                />
              </Button>
              {open ? (
                <div className="pt-2">
                  {history.isPending ? (
                    <Skeleton className="h-52 rounded-md" />
                  ) : (
                    <PostHistoryChart snapshots={history.data ?? []} />
                  )}
                </div>
              ) : null}
            </>
          ) : null}
          <p className="mt-1 text-muted-foreground text-xs">
            {readings === 0
              ? "No reading yet."
              : readings === 1
                ? "One reading so far, taken when you marked it posted."
                : `${readings} readings so far, daily for the first week then weekly to day 90.`}
            {nextReadText
              ? ` Next reading ${nextReadText}.`
              : readings > 0
                ? " Finished: numbers stop moving after 90 days."
                : ""}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
